import { Dispatch, SetStateAction, Suspense, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { CircleNotch, Funnel, MagnifyingGlass } from "@phosphor-icons/react"
import { debounce } from "es-toolkit/function"
import { fetchQuery, graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SuggestionSearch } from "@/components/ui/search"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useClickAway } from "@/hooks/useClickAway"
import { cn } from "@/lib/utils"
import {
  AdvancedCareProviderSearchDialog,
  DefaultAdvancedCareProviderSearchFilters,
} from "@/modules/care-provider/advanced-search/advanced-care-provider-search-dialog"
import { DEFAULT_ADVANCED_FILTERS_BY_SEARCH_TYPE } from "@/modules/care-provider/advanced-search/constants/default-advanced-filters-by-search-type"
import CareProviderSearchError from "@/modules/care-provider/components/care-provider-search-error"
import { UnknownCareProviderItem } from "@/modules/care-provider/components/unknown-care-provider-item"
import { type careProviderSearchDoctorNetworksQuery } from "@/modules/care-provider/simple-search/__generated__/careProviderSearchDoctorNetworksQuery.graphql"
import {
  careProviderSearchQuery,
  careProviderSearchQuery$data,
} from "@/modules/care-provider/simple-search/__generated__/careProviderSearchQuery.graphql"
import { CareProviderItem } from "@/modules/care-provider/simple-search/care-provider-item"
import { CareProviderValue } from "@/modules/care-provider/simple-search/care-provider-value"
import {
  AddressInput,
  useCareProviderQuickListQuery$data,
} from "@/modules/care-provider/simple-search/services/__generated__/useCareProviderQuickListQuery.graphql"
import {
  type ApplicableInsuranceCareProviderListIds,
  useChCareProvidersLazyQuery,
} from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { useUnknownCareProviderQuery } from "@/modules/care-provider/simple-search/services/useUnknownCareProviderQuery"
import { createCareProviderQuickListQueryVariables } from "@/modules/care-provider/utils/create-care-provider-quick-list-query-variables"
import { formatPastedCareProviderPhoneNumber } from "@/modules/care-provider/utils/format-pasted-care-provider-phone-number"
import { BetterDocManualSearchBanner } from "@/modules/patients/detail/better-doc/components/better-doc-manual-search-banner"
import { useBetterDocSuggestionsStoreContext } from "@/modules/patients/detail/better-doc/store/useBetterDocSuggestionsStore"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { RelayEnvironment } from "@/relay-environment"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { BetterDocResultStatus, CareProviderSearchType, TimeToTreatType } from "@/types/__generated__/union-types"

const NO_APPLICABLE_INSURANCE_CARE_PROVIDER_LIST_IDS: ApplicableInsuranceCareProviderListIds = []

const searchCareProviderQuery = graphql`
  query careProviderSearchQuery($searchInput: CareProviderSearchInput!, $language: String!) {
    ch_careProviderSearch(searchInput: $searchInput) {
      results {
        id
        firstName
        lastName
        businessName
        title
        careProviderGroupId
        countryCode
        city
        postalCode
        streetAddress
        country {
          countryName(language: $language)
        }
        creditorCodeRegister
        careProviderGroupQuickList {
          groupType
        }

        isTakesNewPatients
        isMedgatePartnerNetworkMember
        insuranceCareProviderListIds
        groupName(language: $language)
        careProviderSpecialtyTranslations(language: $language) {
          name
        }
        relationType
        partyType
        relationBusinessName
        relationFirstName
        relationLastName
        relationTitle
        detailedSchedule {
          day
          workingType
          periods {
            start
            end
          }
        }
        highlights {
          key
          value
        }
      }
    }
  }
`

const careProviderSearchDoctorNetworksQuery = graphql`
  query careProviderSearchDoctorNetworksQuery($patientId: ID!) {
    ch_patientById(id: $patientId) {
      doctorNetworks {
        id
        role
        careProvider {
          id
          firstName
          lastName
          title
          businessName
          creditorCodeRegister
          city
          postalCode
          streetAddress
        }
      }
    }
  }
`

type CareProviderSearchProps = {
  label: string
  name: string
  value: string | string[] | null | undefined
  isMultiple?: boolean
  error?: string
  disabled?: boolean
  hasMPNReferral?: boolean
  showInsurerNetwork?: boolean
  patientAddress?: AddressInput | null
  specialtyId?: string
  searchType: CareProviderSearchType
  timeToTreat?: TimeToTreatType
  allowUnknownCareProvider?: boolean
  defaultAdvancedSearchFilters?: DefaultAdvancedCareProviderSearchFilters
  episodeOfCareProgressNoteData?: EpisodeOfCareProgressNoteData
  betterDocSuggestionStatus?: BetterDocResultStatus
  isDoctorNetwork?: boolean

  minChars?: number
  maxItems?: number
  analyticsSectionName: string

  onRemove: (value?: string) => void
  onSelect: (value: string) => void
  onSetBetterDocSuggestion?: () => void
  onFocus?: (callback: VoidFunction, event: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => void
}

export type CareProviderOption =
  | useCareProviderQuickListQuery$data["ch_careProviderQuickList"]["personalCareProviders"][number]
  | careProviderSearchQuery$data["ch_careProviderSearch"]["results"][number]

const CareProviderSearch = ({
  label,
  name,
  value,
  isMultiple = false,
  error,
  disabled,
  searchType,
  timeToTreat,
  hasMPNReferral = false,
  showInsurerNetwork = false,
  patientAddress,
  specialtyId,
  defaultAdvancedSearchFilters,
  allowUnknownCareProvider = false,
  episodeOfCareProgressNoteData,
  betterDocSuggestionStatus,
  minChars = 3,
  maxItems,
  analyticsSectionName,
  isDoctorNetwork,
  onSelect,
  onRemove,
  onSetBetterDocSuggestion,
  onFocus,
}: CareProviderSearchProps) => {
  const { t, i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const [searchValue, setSearchValue] = useState("")
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const [searchList, setSearchList] = useState<readonly CareProviderOption[] | null>(null)
  const searchWrapperRef = useRef<HTMLDivElement>(null)
  const activeRequestRef = useRef<{ unsubscribe: () => void } | null>(null)

  const searchRef = useRef<HTMLInputElement>(null)
  const [isOpenAdvancedCareProviderSearchDialog, setIsOpenAdvancedCareProviderSearchDialog] = useState(false)

  const isBetterDocEmpty = useBetterDocSuggestionsStoreContext((store) =>
    store.isBetterDocEmpty(episodeOfCareProgressNoteData?.id),
  )

  useEffect(() => {
    setSearchValue("")
  }, [i18n.language])

  useEffect(() => {
    return () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.unsubscribe()
      }
    }
  }, [])

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      e.preventDefault()
      const pastedText = e.clipboardData.getData("text")
      const formattedText = formatPastedCareProviderPhoneNumber(pastedText)

      const { value, selectionStart, selectionEnd } = e.target

      if (selectionStart === null || selectionEnd === null) return

      const newValue = value.substring(0, selectionStart) + formattedText + value.substring(selectionEnd)

      handleSearchInputChange(newValue)
    }
  }

  const handleFocus = (event?: KeyboardEvent) => {
    setSearchResultVisible(true)
    if (event?.code === "Escape" || event?.code === "Tab") setSearchResultVisible(false)
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length < minChars) {
          setSearchList(null)
          return
        }

        if (activeRequestRef.current) {
          activeRequestRef.current.unsubscribe()
        }

        activeRequestRef.current = fetchQuery<careProviderSearchQuery>(RelayEnvironment, searchCareProviderQuery, {
          language: i18n.language.toUpperCase(),
          searchInput: {
            top: 50,
            searchText: value,
            searchType,
            specialtyId,
            timeToTreatSort: timeToTreat,
          },
        }).subscribe({
          start: () => {
            setIsLoading(true)
          },
          next: (response) => {
            setSearchList(response?.ch_careProviderSearch.results ?? [])
            setSearchResultVisible(true)
            setIsLoading(false)
            activeRequestRef.current = null
          },
          error: () => {
            setIsLoading(false)
            activeRequestRef.current = null
          },
        })
      }, 800),
    [i18n.language, minChars, searchType, specialtyId, timeToTreat],
  )

  const handleSearchInputChange = (value: string) => {
    setSearchValue(value)
    debouncedSearch(value)
  }

  const handleSelect = (id: string) => {
    onSelect(id)
    setSearchResultVisible(false)
    if (!isMultiple) setSearchValue("")

    setTimeout(() => {
      const careProviderValueElement = searchWrapperRef.current?.querySelector<HTMLDivElement>("#care-provider-value")
      careProviderValueElement?.focus()
    }, 350)
  }

  const closeAdvancedCareProviderSearchDialog = () => {
    setIsOpenAdvancedCareProviderSearchDialog(false)
    FaroInsightsWrapper.pushEvent("care_provider_advanced_search_close")
  }

  const normalizeValue = (val: string | string[] | undefined | null): string[] => {
    if (!val) return []

    return Array.isArray(val) ? val : [val]
  }

  const selectedValues: string[] = normalizeValue(value)

  const shouldShowInput = isMultiple
    ? selectedValues.length < (typeof maxItems === "number" ? maxItems : Infinity)
    : selectedValues.length === 0

  if (betterDocSuggestionStatus === "MANUAL_SEARCH_NEEDED") {
    return <BetterDocManualSearchBanner canEdit={true} onClear={onRemove} />
  }

  return (
    <SuggestionSearch ref={searchWrapperRef}>
      {/* Input */}
      {shouldShowInput && (
        <div className="relative">
          <Button
            data-testid="care-provider-search-filter-button"
            onClick={(event) => {
              if (onFocus) {
                onFocus(() => setIsOpenAdvancedCareProviderSearchDialog(true), event)
              } else {
                setIsOpenAdvancedCareProviderSearchDialog(true)
              }

              FaroInsightsWrapper.pushEvent("care_provider_advanced_search_open")
            }}
            variant="circle"
            type="button"
            disabled={disabled}
            tabIndex={-1}
            className="absolute top-1/2 right-10 h-10 w-10 -translate-y-1/2 p-0">
            <Funnel className="text-on-surface-subdued" />
          </Button>
          <label
            data-testid="care-provider-search"
            className="pointer-events-none absolute top-1/2 right-1 -translate-y-1/2 p-2">
            {isLoading ? (
              <CircleNotch className="text-primary animate-spin" />
            ) : (
              <MagnifyingGlass className="text-on-surface-subdued" />
            )}
          </label>
          <input
            placeholder={label}
            id={`${name}-search`}
            ref={searchRef}
            value={searchValue}
            className={cn(
              "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed",
              error && "outline-critical! outline-1 outline-offset-0 focus:outline-2",
            )}
            data-testid="care-provider-search-input"
            autoComplete="off"
            onChange={({ target }) => handleSearchInputChange(target.value)}
            onPaste={onPaste}
            disabled={disabled}
            onFocus={(event) => {
              if (onFocus) {
                onFocus(handleFocus, event)
              } else {
                handleFocus()
              }
              FaroInsightsWrapper.pushEvent(`${analyticsSectionName}_care_provider_search_focus`)
            }}
            onBlur={() => {
              FaroInsightsWrapper.pushEvent(`${analyticsSectionName}_care_provider_search_blur`)
            }}
          />
        </div>
      )}

      {/* Selected value */}
      <Suspense fallback={<p className="h-12 rounded-xl border" />}>
        <SelectedValuesWrapper
          selectedValues={selectedValues}
          isMultiple={isMultiple}
          disabled={disabled}
          hasMPNReferral={hasMPNReferral}
          searchType={searchType}
          specialtyId={specialtyId}
          showInsurerNetwork={showInsurerNetwork}
          patientAddress={patientAddress}
          episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
          handleSelect={handleSelect}
          onSetBetterDocSuggestion={onSetBetterDocSuggestion}
          onRemove={onRemove}
          setSearchValue={setSearchValue}
        />
      </Suspense>

      <Suspense fallback={null}>
        <Result
          selectedValues={selectedValues}
          config={{
            allowUnknownCareProvider,
            hasMPNReferral,
            showInsurerNetwork,
            patientAddress,
            minChars,
            defaultAdvancedSearchFilters,
            isDoctorNetwork,
            searchType,
            specialtyId,
          }}
          state={{
            searchList,
            searchValue,
            isLoading,
            searchResultVisible,
            isOpenAdvancedCareProviderSearchDialog,
          }}
          actions={{
            setSearchResultVisible,
            closeAdvancedCareProviderSearchDialog,
            handleSelect,
          }}
        />
      </Suspense>
      <CareProviderSearchError
        betterDocError={
          isBetterDocEmpty && selectedValues.length === 0
            ? t("episodeOfCare.progressNote.pointOfCare.betterDocNoResult")
            : undefined
        }
        error={error ? t(error) : undefined}
      />
    </SuggestionSearch>
  )
}

type SearchConfig = {
  allowUnknownCareProvider?: boolean
  hasMPNReferral?: boolean
  showInsurerNetwork: boolean
  patientAddress?: AddressInput | null
  minChars: number
  defaultAdvancedSearchFilters?: DefaultAdvancedCareProviderSearchFilters
  isDoctorNetwork?: boolean
  searchType: CareProviderSearchType
  specialtyId?: string
}

type SearchState = {
  searchList: readonly CareProviderOption[] | null
  searchValue: string
  isLoading: boolean
  searchResultVisible: boolean
  isOpenAdvancedCareProviderSearchDialog: boolean
}

type SearchActions = {
  setSearchResultVisible: Dispatch<SetStateAction<boolean>>
  closeAdvancedCareProviderSearchDialog: () => void
  handleSelect: (id: string) => void
}

type ResultProps = {
  selectedValues: string[]
  config: SearchConfig
  state: SearchState
  actions: SearchActions
}

const Result = ({ selectedValues, config, state, actions }: ResultProps) => {
  const {
    allowUnknownCareProvider,
    hasMPNReferral,
    showInsurerNetwork,
    patientAddress,
    minChars,
    defaultAdvancedSearchFilters,
    isDoctorNetwork,
    searchType,
    specialtyId,
  } = config
  const { searchList, searchValue, isLoading, searchResultVisible, isOpenAdvancedCareProviderSearchDialog } = state
  const { setSearchResultVisible, closeAdvancedCareProviderSearchDialog, handleSelect } = actions
  const { t, i18n } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const patientId = usePatientParam()
  const { ch_unknownCareProvider } = useUnknownCareProviderQuery({ skip: !allowUnknownCareProvider })
  const { ch_careProviderQuickList } = useChCareProvidersLazyQuery(
    createCareProviderQuickListQueryVariables({
      patientId,
      language: i18n.language,
      searchType,
      specialtyId,
      showInsurerNetwork,
      patientAddress,
    }),
  )
  const applicableInsuranceCareProviderListIds =
    ch_careProviderQuickList.insurerNetwork?.applicableInsuranceCareProviderListIds ??
    NO_APPLICABLE_INSURANCE_CARE_PROVIDER_LIST_IDS

  const { ch_patientById } = useLazyLoadQuery<careProviderSearchDoctorNetworksQuery>(
    careProviderSearchDoctorNetworksQuery,
    {
      patientId,
    },
  )
  const doctorNetworks = ch_patientById?.doctorNetworks

  const quickList = ch_careProviderQuickList.personalCareProviders
  const selectedDoctorNetworkIds = doctorNetworks?.map((item) => item.careProvider.id) ?? []

  const searchResultsRef = useClickAway(closeSearchResults)

  const filteredQuickList = quickList?.filter((item) => !selectedValues.includes(item.id))
  const filteredSearchList = searchList?.filter((item) => !selectedValues.includes(item.id)) ?? null

  const hasQuickList = !searchValue.length && filteredQuickList.length > 0
  const insurerNetworkOptions = ch_careProviderQuickList.insurerNetwork?.careProviders ?? []
  const hasInsurerNetwork = !searchValue.length && insurerNetworkOptions.length > 0
  const hasSearchResults =
    Boolean(filteredSearchList && filteredSearchList?.length > 0) && searchValue.length >= minChars
  const hasUnknownCareProvider = !!ch_unknownCareProvider
  const showNothingFound =
    searchValue.length >= minChars && !hasQuickList && filteredSearchList?.length === 0 && !hasUnknownCareProvider
  const showSearchContent =
    !isLoading &&
    searchResultVisible &&
    (hasQuickList || hasInsurerNetwork || hasSearchResults || hasUnknownCareProvider || showNothingFound)
  const showPersonalCareProvidersTitle = hasQuickList && (hasUnknownCareProvider || hasInsurerNetwork)

  function closeSearchResults() {
    setSearchResultVisible(false)
  }

  function handleUnknownSelect(value: string) {
    confirm({
      title: t("admin.careProviders.unknownCareProvider.documentUnknownCareProvider"),
      confirmText: t("admin.careProviders.unknownCareProvider.documentUnknownCareProvider"),
      cancelText: t("common.button.cancel"),
      content: t("admin.careProviders.unknownCareProvider.description"),
      onConfirm() {
        handleSelect(value)
        cancel()
      },
    })
  }

  return (
    <>
      {showSearchContent && (
        <SearchContent
          ref={searchResultsRef}
          className="px-0 py-2 [&>div]:overflow-hidden"
          data-testid="care-provider-search-results">
          {showNothingFound && <p className="text-on-surface-subdued px-4">{t("common.nothingFound")}</p>}
          {(hasQuickList || hasInsurerNetwork || hasSearchResults) && (
            <ScrollArea viewportClassName="max-h-96 px-2 py-2 [&>div]:block!">
              {hasQuickList && (
                <div>
                  {showPersonalCareProvidersTitle && (
                    <p className="text-on-surface-subdued bg-surface sticky -top-2 z-10 px-2 pt-4 pb-0.5 text-sm leading-5 font-medium">
                      {t("admin.careProviders.personalCareProviders")}
                    </p>
                  )}
                  {filteredQuickList.map((option) => (
                    <CareProviderItem
                      key={option.id}
                      option={option}
                      doctorNetworks={doctorNetworks}
                      hasMPNReferral={hasMPNReferral}
                      applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}

              {hasInsurerNetwork && (
                <div>
                  <p
                    className="text-on-surface-subdued bg-surface sticky -top-2 z-10 px-2 pt-4 pb-0.5 text-sm leading-5 font-medium"
                    data-testid="care-provider-insurer-network-section">
                    {t("admin.careProviders.insurerNetwork")}
                  </p>
                  {insurerNetworkOptions.map((option) => (
                    <CareProviderItem
                      key={option.id}
                      option={option}
                      doctorNetworks={doctorNetworks}
                      hasMPNReferral={hasMPNReferral}
                      applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}

              {hasSearchResults &&
                filteredSearchList?.map((option) => (
                  <CareProviderItem
                    key={option.id}
                    option={option}
                    doctorNetworks={doctorNetworks}
                    hasMPNReferral={hasMPNReferral}
                    applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
                    onSelect={handleSelect}
                  />
                ))}
            </ScrollArea>
          )}

          {hasUnknownCareProvider && (
            <UnknownCareProviderItem
              hasSearchSeparator={hasQuickList || hasInsurerNetwork || hasSearchResults}
              onSelect={() => handleUnknownSelect(ch_unknownCareProvider.id)}
            />
          )}
        </SearchContent>
      )}
      {isOpenAdvancedCareProviderSearchDialog ? (
        <Suspense fallback={null}>
          <AdvancedCareProviderSearchDialog
            defaultFilters={{
              ...defaultAdvancedSearchFilters,
              ...DEFAULT_ADVANCED_FILTERS_BY_SEARCH_TYPE[searchType],
            }}
            applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
            onClose={closeAdvancedCareProviderSearchDialog}
            selectedCareProviderIds={isDoctorNetwork ? selectedDoctorNetworkIds : selectedValues}
            onCareProviderSelect={(id) => {
              handleSelect(id)
              closeAdvancedCareProviderSearchDialog()
            }}
          />
        </Suspense>
      ) : null}
    </>
  )
}

type SelectedValuesWrapperProps = {
  selectedValues: string[]
  isMultiple: boolean
  disabled?: boolean
  hasMPNReferral: boolean
  searchType: CareProviderSearchType
  specialtyId?: string
  showInsurerNetwork: boolean
  patientAddress?: AddressInput | null
  episodeOfCareProgressNoteData?: EpisodeOfCareProgressNoteData
  handleSelect: (id: string) => void
  onSetBetterDocSuggestion?: () => void
  onRemove: (value?: string) => void
  setSearchValue: (value: string) => void
}

const SelectedValuesWrapper = (props: SelectedValuesWrapperProps) => {
  if (props.selectedValues.length === 0) {
    return null
  }

  return <SelectedCareProviderValues {...props} />
}

const SelectedCareProviderValues = ({
  selectedValues,
  isMultiple,
  disabled,
  hasMPNReferral,
  searchType,
  specialtyId,
  showInsurerNetwork,
  patientAddress,
  episodeOfCareProgressNoteData,
  handleSelect,
  onSetBetterDocSuggestion,
  onRemove,
  setSearchValue,
}: SelectedValuesWrapperProps) => {
  const { i18n } = useTranslation()
  const patientId = usePatientParam()
  const { ch_careProviderQuickList } = useChCareProvidersLazyQuery(
    createCareProviderQuickListQueryVariables({
      patientId,
      language: i18n.language,
      searchType,
      specialtyId,
      showInsurerNetwork,
      patientAddress,
    }),
    "store-or-network",
  )
  const applicableInsuranceCareProviderListIds =
    ch_careProviderQuickList.insurerNetwork?.applicableInsuranceCareProviderListIds ??
    NO_APPLICABLE_INSURANCE_CARE_PROVIDER_LIST_IDS
  const { ch_patientById } = useLazyLoadQuery<careProviderSearchDoctorNetworksQuery>(
    careProviderSearchDoctorNetworksQuery,
    {
      patientId,
    },
  )
  const doctorNetworks = ch_patientById?.doctorNetworks

  return (
    <div className={cn("space-y-2", isMultiple && "mt-2")}>
      {selectedValues.map((value) => (
        <CareProviderValue
          hasMPNReferral={hasMPNReferral}
          applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
          doctorNetworks={doctorNetworks}
          key={value}
          value={value}
          viewOnly={disabled}
          progressNoteId={episodeOfCareProgressNoteData?.id}
          onSelect={({ id }) => handleSelect(id)}
          onSetBetterDocSuggestion={onSetBetterDocSuggestion}
          onRemove={() => {
            onRemove(value)
            setSearchValue("")
          }}
        />
      ))}
    </div>
  )
}

export { CareProviderSearch, careProviderSearchDoctorNetworksQuery }
